import { type JSX, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { assert } from '@chainflip/utils/assertion';
import { sleep } from '@chainflip/utils/async';
import { assetConstants, type ChainflipAsset } from '@chainflip/utils/chainflip';
import { isNotNullish } from '@chainflip/utils/guard';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BigNumber } from 'bignumber.js';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'motion/react';
import { z } from 'zod';
import { validateChainAddress } from '@/shared/assets/chains/addressValidation';
import { type ChainflipToken } from '@/shared/assets/tokens';
import AddressInput from '@/shared/components/AddressInput';
import { SkeletonLine } from '@/shared/components/atoms/LoadingSkeleton';
import { DashboardModalWithCloseButton } from '@/shared/components/dashboards/Modal';
import { ReadonlyButton } from '@/shared/components/dashboards/Readonly';
import { Divider } from '@/shared/components/Divider';
import TokenSelectDropdown from '@/shared/components/dropdown/TokenSelectDropdown';
import Button from '@/shared/components/flip-ui-kit/Button';
import Pill from '@/shared/components/flip-ui-kit/Pill';
import TokenInputAmount from '@/shared/components/input/TokenInputAmount';
import { ApyPeriodSelect } from '@/shared/components/lending/ApyPeriodSelect';
import BorrowRewardsTooltip from '@/shared/components/lending/BorrowRewardsTooltip';
import { BorrowSlider } from '@/shared/components/lending/BorrowSlider';
import { type RewardsTooltipProps } from '@/shared/components/lending/SupplyRewardsTooltip';
import { TokenLogo } from '@/shared/components/logo/TokenLogo';
import { TokenWithChainByAsset } from '@/shared/components/logo/TokenWithChain';
import IconButton from '@/shared/components/molecules/IconButton';
import { Tooltip } from '@/shared/components/molecules/Tooltip';
import QuestionMarkTooltip from '@/shared/components/QuestionMarkTooltip';
import { useIncentives } from '@/shared/hooks/useIncentives';
import { useLendingConfig } from '@/shared/hooks/useLendingConfig';
import { useLendingPools } from '@/shared/hooks/useLendingPools';
import { useLoanRiskType, LoanRiskTypes } from '@/shared/hooks/useLoanRiskType';
import { useOraclePrices } from '@/shared/hooks/useOraclePrice';
import ArrowIconLarge from '@/shared/icons/flip-ui-kit/large/Arrow';
import CloseIcon from '@/shared/icons/flip-ui-kit/large/Close';
import SparklesIcon from '@/shared/icons/flip-ui-kit/small/Sparkles';
import WindowIcon from '@/shared/icons/flip-ui-kit/small/Window';
import { chainLabel } from '@/shared/utils/chainflip';
import { defaultAnimationProps } from '@/shared/utils/consts';
import { chainflipAssetMap, chainflipChainMap } from '@/shared/utils/env';
import {
  type BorrowApyPeriodOption,
  BORROW_APY_PERIODS,
  calculateLowLtvPenaltyPercent,
  calculateLoanToValueBps,
  DEFAULT_APY_PERIOD,
} from '@/shared/utils/lending';
import {
  formatUsdValue,
  pipsToPercentString,
  pipsToPercent,
  percentToPips,
} from '@/shared/utils/numbers';
import { formatBps, removeCommas } from '@/shared/utils/strings';
import TokenAmount from '@/shared/utils/TokenAmount';
import useLoanAccount from '../../../hooks/useLoanAccount';
import useRefundAddresses from '../../../hooks/useRefundAddresses';
import { useExtrinsicSDK, useStateChainAccount } from '../../../hooks/useStateChainAccount';
import SuccessIllustration from '../../../public/success-illustration.svg';
import { RewardsCallout } from '../../Rewards/RewardsCallout';

const feeType = z.string().transform((val) => BigInt(removeCommas(val)));
const originationFeeTakenSchema = z.object({
  loanId: z.string(),
  poolFee: feeType,
  networkFee: feeType,
  brokerFee: feeType.default('0'), // brokerFee gets removed in 2.3 from the origination fee
});

type PreviousState = {
  requestedBorrowAmount: TokenAmount;
  requestedBorrowAmountUsd: number;
  oldBorrowAmount?: TokenAmount;
  newBorrowAmount?: TokenAmount;
  newBorrowAmountUsd: number;
  selectedToken: ChainflipToken;
  loanToValueBps: number;
  interestRateBps: number;
  penaltyFeePercent?: number;
  rewardsData?: RewardsTooltipProps;
};

interface BorrowModalProps {
  open: boolean;
  onClose: () => void;
  selectedToken: ChainflipToken;
  setSelectedToken: (token: ChainflipToken) => void;
  onAddSupplyClick: () => void;
}

const InterestRateText = ({
  interestRateBps,
  penaltyFeePercent,
  rewardsData,
}: {
  interestRateBps: number;
  penaltyFeePercent: number;
  rewardsData?: RewardsTooltipProps;
}) => (
  <div className="flex flex-wrap items-center gap-1">
    {rewardsData ? (
      <Tooltip
        content={<BorrowRewardsTooltip {...rewardsData} />}
        tooltipClassName="!bg-cf-gray-3-5 !border-cf-gray-4"
      >
        <div className="flex items-center gap-x-1">
          <SparklesIcon className="text-cf-green-1" />
          {pipsToPercentString(interestRateBps)}
        </div>
      </Tooltip>
    ) : (
      pipsToPercentString(interestRateBps)
    )}

    {penaltyFeePercent > 0 && (
      <Tooltip content="An additional interest charge applied when your LTV is below 50%. It scales up to 1% APY at 0% LTV.">
        <span className="text-cf-red-2">(+{penaltyFeePercent.toFixed(2)}%)</span>
      </Tooltip>
    )}
  </div>
);

const Success = ({
  title,
  borrowToken,
  borrowAmount,
  borrowAmountUsd,
  children,
}: {
  title: string;
  borrowToken: ChainflipToken;
  borrowAmount: TokenAmount;
  borrowAmountUsd: number;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-6 rounded-md">
    <div className="my-auto flex w-full flex-col items-center justify-center">
      <div className="mb-[-74px]">
        <SuccessIllustration />
      </div>
      <div className="flex flex-col items-center gap-y-2 font-aeonikMedium">
        <span className="text-24 text-cf-white">{title}</span>
        <span className="flex items-center gap-x-1">
          You borrowed {borrowAmount.toFormatted()}
          <TokenWithChainByAsset asset={borrowToken.chainflipId} />
          {chainflipAssetMap[borrowToken.chainflipId].symbol}
          {isNotNullish(borrowAmountUsd) && (
            <span className="text-cf-light-2">({formatUsdValue(borrowAmountUsd)})</span>
          )}
        </span>
      </div>
    </div>
    {children}
  </div>
);

export const BorrowModal = ({
  open,
  onClose,
  selectedToken,
  setSelectedToken,
  onAddSupplyClick,
}: BorrowModalProps): JSX.Element => {
  const router = useRouter();

  const refundAddresses = useRefundAddresses();
  const { toast: requestLoanToast, sendExtrinsic: requestLoan } = useExtrinsicSDK('requestLoan');
  const { toast: expandLoanToast, sendExtrinsic: expandLoan } = useExtrinsicSDK('expandLoan');
  const { sendExtrinsic: sendBatch } = useExtrinsicSDK('signAndSendBatch');
  const {
    lendingPools,
    poolAssets,
    poolBalances,
    isLoading: isPoolBalanceLoading,
  } = useLendingPools();
  const { refreshAccountInfo } = useStateChainAccount();
  const { loanAccount, isLoading: isLoanAccountLoading } = useLoanAccount();
  const { loanToValueBps, isLiquidating, totalBorrowAmountUsd, totalCollateralBalanceUsd, loans } =
    loanAccount;
  const {
    minLoanRequestAmountUsd,
    minLoanUpdateAmountUsd,
    targetLtvThresholdBps,
    softLiquidationLtvThresholdBps,
    lowLtvPenaltyBpsAtMaxLtv,
    lowLtvPenaltyThresholdBps,
    isLoading: isConfigLoading,
  } = useLendingConfig();
  const prices = useOraclePrices();
  const [error, setError] = useState<string>();
  const [emptyAmountError, setEmptyAmountError] = useState<string>();
  const [sliderValue, setSliderValue] = useState<number>(0);
  const [inputAmount, setInputAmount] = useState<TokenAmount>();
  const [isTokenSelectOpen, setIsTokenSelectOpen] = useState(false);
  const [borrowApyPeriod, setBorrowApyPeriod] = useState<BorrowApyPeriodOption>(DEFAULT_APY_PERIOD);
  const [sliderFocused, setSliderFocused] = useState<boolean>(false);
  const [view, setView] = useState<'form' | 'setRefundAddress' | 'success'>('form');
  const [address, setAddress] = useState('');
  const [addressInputError, setAddressInputError] = useState<string>();
  const { chainflipId: selectedAsset, decimals: selectedAssetDecimals } = selectedToken;
  const { chain } = assetConstants[selectedAsset];
  const refundAddress = refundAddresses?.[chain as Exclude<typeof chain, 'Polkadot'>];

  const [prevValues, setPrevValues] = useState<PreviousState>();
  const queryClient = useQueryClient();

  const [extrinsicResult, setExtrinsicResult] = useState<{
    feeAmount: TokenAmount;
    feeAmountUsd: number;
  }>({ feeAmount: TokenAmount.ZERO, feeAmountUsd: 0 });
  const offsetedTargetLtvThresholdPercent = pipsToPercent(targetLtvThresholdBps);

  const singleLoan = useMemo(() => {
    const loansInSelectedAsset = loans?.filter((loan) => loan.asset === selectedAsset);

    return loansInSelectedAsset?.length === 1 ? loansInSelectedAsset[0] : undefined;
  }, [loans, selectedAsset]);

  const selectedTokenPrice = prices?.[selectedAsset]?.usdPrice;

  const { riskType, riskColor } = useLoanRiskType(sliderValue);

  const { activeSeason } = useIncentives();
  const borrowIncentive = activeSeason?.pools.find(
    (pool) => pool.asset === selectedToken.chainflipId && pool.type === 'BORROW',
  );

  const selectedPool = lendingPools.find((p) => p.asset === selectedAsset);
  const { originationFeeBps: currentOriginationFeeBps } = selectedPool ?? { originationFeeBps: 0 };
  const selectedBorrowApyPercent =
    selectedPool?.projectedBorrowApyByPeriod[borrowApyPeriod.value] ?? 0;
  const borrowApyTooltip =
    borrowApyPeriod.value === 'CURRENT'
      ? 'The current interest rate charged to new borrows in this market.'
      : `The average annualized borrowing rate across the market over the past ${borrowApyPeriod.description}.`;
  const incentiveRateBps = percentToPips(borrowIncentive?.apy ?? 0);
  const totalBorrowRateBps = percentToPips(selectedBorrowApyPercent) - incentiveRateBps;
  const rewardsData = borrowIncentive
    ? {
        seasonIndex: activeSeason?.seasonIndex ?? 0,
        baseApy: selectedBorrowApyPercent,
        rewardsApy: borrowIncentive.apy,
        dailyPoints: borrowIncentive.dailyPoints,
      }
    : undefined;

  const poolTotalAvailableAmount = poolBalances[selectedAsset]?.totalAvailableAmount;
  const poolTotalBorrowableAmount = poolBalances[selectedAsset]?.totalBorrowableAmount;
  const poolTotalBorrowableAmountUsd =
    poolTotalBorrowableAmount?.toUsdNumber(selectedTokenPrice) ?? 0;

  const estimatedOriginationFee =
    inputAmount?.mul(currentOriginationFeeBps).div(10_000) ?? TokenAmount.ZERO;

  const estimatedOriginationFeeUsd = estimatedOriginationFee.toUsdNumber(selectedTokenPrice);

  const [lowLtvPenaltyThresholdPercent, sliderValuePercent, lowLtvPenaltyPercentAtMaxLtv] = [
    pipsToPercent(lowLtvPenaltyThresholdBps),
    pipsToPercent(sliderValue),
    pipsToPercent(lowLtvPenaltyBpsAtMaxLtv),
  ];
  const penaltyFeePercent = calculateLowLtvPenaltyPercent(
    sliderValuePercent,
    lowLtvPenaltyThresholdPercent,
    lowLtvPenaltyPercentAtMaxLtv,
  );

  const borrowPowerUsd = (totalCollateralBalanceUsd * offsetedTargetLtvThresholdPercent) / 100;
  const borrowCapacityUsd = Math.max(borrowPowerUsd - totalBorrowAmountUsd, 0);
  const borrowCapacity = TokenAmount.fromWholeUnits(
    selectedTokenPrice ? borrowCapacityUsd / selectedTokenPrice : 0,
    selectedAssetDecimals,
  );

  const borrowCapacityPerLoanAssetMap = useMemo(() => {
    const map = {} as Record<ChainflipAsset, TokenAmount>;
    for (const asset of poolAssets) {
      const assetPrice = prices?.[asset]?.usdPrice ?? 0;
      const capacity = TokenAmount.fromWholeUnits(
        borrowCapacityUsd / assetPrice,
        chainflipAssetMap[asset].decimals,
      );
      map[asset] = capacity;
    }
    return map;
  }, [
    offsetedTargetLtvThresholdPercent,
    poolAssets,
    totalCollateralBalanceUsd,
    totalBorrowAmountUsd,
    prices,
  ]);

  const resetForm = () => {
    setSliderValue(loanToValueBps);
    setInputAmount(undefined);
  };

  const {
    mutate: borrow,
    isPending,
    reset: resetMutation,
  } = useMutation({
    mutationFn: async () => {
      const requestedBorrowAmount = inputAmount as TokenAmount; // ctaClickHandler ensures inputAmount is defined
      const newBorrowAmount = requestedBorrowAmount?.add(singleLoan?.principalAmount ?? '0');
      setPrevValues({
        requestedBorrowAmount,
        requestedBorrowAmountUsd: requestedBorrowAmount.toUsdNumber(selectedTokenPrice),
        oldBorrowAmount: singleLoan ? singleLoan.principalAmount : undefined,
        newBorrowAmount,
        newBorrowAmountUsd: newBorrowAmount!.toUsdNumber(selectedTokenPrice),
        selectedToken,
        loanToValueBps,
        interestRateBps: totalBorrowRateBps,
        penaltyFeePercent,
        rewardsData,
      });

      let data;

      if (address) {
        data = await sendBatch((batchSdk) => {
          batchSdk.setRefundAddress(assetConstants[selectedAsset].chain, address);

          batchSdk.requestLoan(selectedAsset, requestedBorrowAmount.toString());
        });
      } else {
        data = await (singleLoan
          ? expandLoan(BigInt(singleLoan.id), requestedBorrowAmount.toString())
          : requestLoan(selectedAsset, requestedBorrowAmount.toString()));
      }

      return { data, submittedAmount: requestedBorrowAmount };
    },
    async onSuccess({ data, submittedAmount }) {
      (singleLoan ? expandLoanToast : requestLoanToast).success(
        `Successfully borrowed${singleLoan ? ' additional' : ''} ${submittedAmount.toFormatted()} ${chainflipAssetMap[selectedAsset].symbol}`,
      );

      if (data) {
        const originationFeeTakenEvent = data.events.find(
          (e) => e.event.toHuman().method === 'OriginationFeeTaken',
        );
        assert(originationFeeTakenEvent, 'OriginationFeeTaken event not returned from extrinsic');

        const { networkFee, brokerFee, poolFee } = originationFeeTakenSchema.parse(
          originationFeeTakenEvent.event.data.toHuman(),
        );
        const totalFee = networkFee + brokerFee + poolFee;
        const feeAmount = TokenAmount.fromAsset(totalFee, selectedAsset);
        const feeAmountUsd = feeAmount.toUsdNumber(selectedTokenPrice ?? 0);

        setExtrinsicResult({
          feeAmount,
          feeAmountUsd,
        });
      }
      await sleep(500);
      await Promise.all(
        ['cf_lending_pools', 'cf_loan_accounts'].map((query) =>
          queryClient.invalidateQueries({ queryKey: [query], exact: false }),
        ),
      );
      await refreshAccountInfo();

      setView('success');
      resetForm();
    },
    onError(err) {
      setPrevValues(undefined);
      (singleLoan ? expandLoanToast : requestLoanToast).error(
        'Error while submitting transaction',
        { description: err.message },
      );
    },
  });

  const handleSliderInput = ([newLtv]: [number]) => {
    const clamped = Math.min(Math.max(newLtv, loanToValueBps), targetLtvThresholdBps);
    setSliderValue(clamped);

    // if it's at max (79.99%), set token input to borrow capacity
    if (clamped === targetLtvThresholdBps) {
      setInputAmount(borrowCapacity);
      return;
    }

    const newInputValueUsd = new BigNumber(pipsToPercent(Math.abs(clamped - loanToValueBps)))
      .multipliedBy(totalCollateralBalanceUsd)
      .div(100);

    const newInputAmount = selectedTokenPrice ? newInputValueUsd.div(selectedTokenPrice) : 0;

    setInputAmount(TokenAmount.fromWholeUnits(newInputAmount, selectedAssetDecimals));
    setEmptyAmountError(undefined);
  };

  const handleTokenInput = (value: TokenAmount | undefined) => {
    setInputAmount(value);

    if (!value) return setSliderValue(loanToValueBps);

    const inputValueUsd = value.mul(selectedTokenPrice ?? 0).toNumber();

    if (isPending) return;

    setSliderValue(
      Math.min(
        calculateLoanToValueBps({
          totalBorrowAmountUsd: inputValueUsd + totalBorrowAmountUsd,
          totalCollateralAmountUsd: totalCollateralBalanceUsd,
        }),
        targetLtvThresholdBps,
      ),
    );

    if (value.gt(0)) setEmptyAmountError(undefined);
  };

  const tokensWithBalances = useMemo(
    () =>
      Object.entries(borrowCapacityPerLoanAssetMap).map(([asset, balance]) => ({
        token: chainflipAssetMap[asset as ChainflipAsset],
        balance: balance ?? TokenAmount.ZERO,
      })) ?? [],
    [borrowCapacityPerLoanAssetMap],
  );

  const ctaClickHandler = () => {
    if (!inputAmount || inputAmount.lte(0)) {
      setEmptyAmountError('Please enter a valid amount');
      return;
    }
    if (!singleLoan && inputAmount.toUsdNumber(selectedTokenPrice) < minLoanRequestAmountUsd) {
      setError(
        `Amount must be greater than the minimum loan creation amount: ${formatUsdValue(minLoanRequestAmountUsd)}`,
      );
      return;
    }
    if (singleLoan && inputAmount.toUsdNumber(selectedTokenPrice) < minLoanUpdateAmountUsd) {
      setError(
        `Amount must be greater than the minimum loan update amount: ${formatUsdValue(minLoanUpdateAmountUsd)}`,
      );
      return;
    }
    if (poolTotalAvailableAmount && inputAmount.gt(poolTotalAvailableAmount)) {
      setError(
        `Amount exceeds the available pool liquidity: ${poolTotalAvailableAmount.toFormatted()} ${selectedToken.symbol}. Please enter a smaller amount.`,
      );
      return;
    }

    if (poolTotalBorrowableAmount && inputAmount.gt(poolTotalBorrowableAmount)) {
      setError(
        `Amount exceeds the available pool liquidity: ${poolTotalBorrowableAmount.toFormatted()} ${selectedToken.symbol}. Please enter a smaller amount.`,
      );
      return;
    }

    if (!refundAddress && !address) {
      setView('setRefundAddress');
    } else {
      borrow();
    }
  };

  const handleAddressChange = (value: string) => {
    setAddress(value);

    if (!value) {
      setAddressInputError('A refund address is required');
      return;
    }

    const isValid = validateChainAddress(value)[chainflipChainMap[chain].id];

    if (!isValid) {
      setAddressInputError(`A valid ${chainLabel(chain)} address is required`);
      return;
    }

    setAddressInputError(undefined);
  };

  return (
    <DashboardModalWithCloseButton
      open={open}
      onClose={onClose}
      afterLeave={() => {
        resetForm();
        setView('form');
        setAddressInputError(undefined);
        setAddress('');
        setError(undefined);
        setEmptyAmountError(undefined);
        setSelectedToken(chainflipAssetMap.Usdc);
        setBorrowApyPeriod(DEFAULT_APY_PERIOD);
        resetMutation();
      }}
      topContent={borrowIncentive ? <RewardsCallout /> : undefined}
    >
      <AnimatePresence mode="wait">
        {view === 'success' && (
          <motion.div key="borrow-success" {...defaultAnimationProps}>
            <Success
              title="Loan Confirmed"
              borrowToken={prevValues?.selectedToken ?? chainflipAssetMap.Usdc}
              borrowAmount={prevValues?.requestedBorrowAmount ?? TokenAmount.ZERO}
              borrowAmountUsd={prevValues?.requestedBorrowAmountUsd ?? 0}
            >
              <div className="flex flex-col gap-y-3 rounded-md border border-cf-gray-4 bg-cf-gray-3-5 p-4 font-aeonikMedium text-14">
                <div className="flex justify-between">
                  <div className="flex items-center gap-x-1  text-cf-light-2">
                    Total Collateral Balance{' '}
                    <QuestionMarkTooltip content="The total value of assets securing your loans. Adding more increases your borrowing capacity and reduces liquidation risk." />
                  </div>
                  <div className="flex items-center gap-x-1 text-cf-light-3">
                    {formatUsdValue(totalCollateralBalanceUsd)}
                  </div>
                </div>
                <div className="flex justify-between">
                  <div className="flex items-center gap-x-1 text-cf-light-2">Debt Amount</div>
                  <div className="flex items-center gap-x-1 text-cf-light-3">
                    {prevValues?.oldBorrowAmount ? (
                      <>
                        <span className="text-cf-light-2">
                          {prevValues.oldBorrowAmount.toFormatted()}
                        </span>
                        <ArrowIconLarge className="text-cf-light-2" />{' '}
                      </>
                    ) : null}
                    {prevValues?.newBorrowAmount && (
                      <>
                        <span>{prevValues.newBorrowAmount.toFormatted()} </span>
                        <TokenLogo token={prevValues.selectedToken} />{' '}
                        {prevValues.selectedToken.symbol}
                        <span className="text-cf-light-2">
                          ({formatUsdValue(prevValues.newBorrowAmountUsd)})
                        </span>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex justify-between">
                  <div className="flex items-center gap-x-2 text-cf-light-2">
                    Loan-to-Value (LTV)
                    <QuestionMarkTooltip content="The ratio between your borrowed amount and the value of your collateral. Higher LTV increases liquidation risk." />
                  </div>

                  <div className="flex items-center gap-x-1 text-cf-light-2">
                    {pipsToPercentString(prevValues?.loanToValueBps ?? 0)}
                    {loanToValueBps && (
                      <>
                        <ArrowIconLarge className="text-cf-light-2" />{' '}
                        <span className="text-white">{pipsToPercentString(loanToValueBps)}</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex justify-between">
                  <div className="flex items-center gap-x-1  text-cf-light-2">
                    <span>{borrowApyPeriod.label}</span>
                    <span>Interest Rate</span>
                    <QuestionMarkTooltip content={borrowApyTooltip} />
                  </div>
                  <div className="flex items-center gap-x-1 font-aeonikMedium text-cf-white">
                    <InterestRateText
                      interestRateBps={prevValues?.interestRateBps ?? 0}
                      penaltyFeePercent={prevValues?.penaltyFeePercent ?? 0}
                      rewardsData={prevValues?.rewardsData}
                    />
                    <IconButton
                      href={`/lending/markets/${prevValues?.selectedToken.chainflipId}`}
                      target="_blank"
                    >
                      <WindowIcon />
                    </IconButton>
                  </div>
                </div>
                {extrinsicResult && prevValues && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-x-1 text-cf-light-2">
                      Origination Fee
                      <QuestionMarkTooltip content="A one-time fee charged when opening or updating a borrow position." />
                    </div>
                    <div className="flex items-center gap-x-1">
                      <div className="flex items-center justify-center gap-x-1">
                        <span>{extrinsicResult.feeAmount.toFormatted()}</span>
                        <TokenLogo token={prevValues?.selectedToken} />
                        {chainflipAssetMap[prevValues?.selectedToken.chainflipId].symbol}
                      </div>

                      <span className="font-aeonikRegular text-cf-light-2">
                        ({formatUsdValue(extrinsicResult.feeAmountUsd)})
                      </span>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-y-2">
                <ReadonlyButton
                  className="w-full"
                  type="secondary-standard"
                  onClick={(e) => {
                    e.stopPropagation();
                    setView('form');
                    setError(undefined);
                    setEmptyAmountError(undefined);
                  }}
                >
                  Borrow more
                </ReadonlyButton>
                <ReadonlyButton
                  className="w-full"
                  type="primary-standard"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                    return router.push('/lending?tab=supply');
                  }}
                >
                  View my Position
                </ReadonlyButton>
              </div>
            </Success>
          </motion.div>
        )}

        {view === 'setRefundAddress' && (
          <motion.div key="refund-modal" {...defaultAnimationProps}>
            <div className="flex flex-col gap-y-6">
              <div className="flex flex-col gap-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-aeonikMedium text-16 text-cf-white">
                    {`Set ${chainLabel(assetConstants[selectedAsset].chain)} refund address`}
                  </span>
                  <button type="button" className="text-cf-white" onClick={onClose}>
                    <CloseIcon />
                  </button>
                </div>
                <div className="text-14 text-cf-light-2">
                  You must provide a refund address for network security. You only need to do this
                  once and can be changed anytime.
                </div>
              </div>
              <Divider className="h-px" />
              <AddressInput
                address={address}
                setAddress={handleAddressChange}
                error={addressInputError}
                placeholder="Refund Address"
              />
              <div className="flex justify-end space-x-2">
                <Button disabled={isPending} type="secondary-standard" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  loading={isPending && 'iconOnly'}
                  disabled={isPending}
                  onClick={() => {
                    if (!addressInputError) setView('form');
                  }}
                >
                  Save
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {view === 'form' && (
          <motion.div key="borrow-form" {...defaultAnimationProps}>
            <div className="flex max-w-[437px] flex-col gap-y-6">
              <div className="flex flex-col gap-y-[8px] font-aeonikMedium">
                <div className="text-20 leading-none text-cf-white">Borrow</div>
                <div className="flex items-center gap-x-1 text-14 text-cf-light-2">
                  <span className="leading-none">Need more borrowing power?</span>
                  <ReadonlyButton
                    type="link-standard"
                    size="small"
                    className="h-auto min-w-fit !p-0 align-middle !text-14"
                    onClick={onAddSupplyClick}
                  >
                    Add Supply
                  </ReadonlyButton>
                </div>
              </div>
              <TokenSelectDropdown
                isOpen={isTokenSelectOpen}
                setIsOpen={setIsTokenSelectOpen}
                selectedToken={selectedToken}
                setSelectedToken={(token) => {
                  resetForm();
                  setError(undefined);
                  setEmptyAmountError(undefined);
                  setSelectedToken(token);
                }}
                tokensWithBalances={tokensWithBalances}
                disabled={isPending}
              />
              <TokenInputAmount
                key={`${sliderFocused ? sliderValue : ''}-${selectedToken.chainflipId}`}
                label="Amount"
                token={selectedToken}
                value={inputAmount}
                onChange={handleTokenInput}
                disabled={!selectedToken || !selectedTokenPrice || isPending}
                balance={borrowCapacity}
                maxUpperLimit={poolTotalAvailableAmount?.min(poolTotalBorrowableAmount)}
                balanceLabel="Borrow Capacity"
                error={error || emptyAmountError}
                setError={setError}
                maxErrorMessage="Amount exceeds borrow capacity"
                showError={!isPending}
              />
              {/* Slider */}
              <div
                className={classNames(
                  'flex flex-col rounded-md border border-cf-gray-4 bg-cf-gray-3-5 p-4 shadow-grayPop3 transition duration-100 ease-in',
                  isTokenSelectOpen && 'opacity-50',
                )}
              >
                <div className="flex justify-between">
                  <div className="flex items-center gap-x-1 font-aeonikMedium text-14 text-cf-light-2">
                    Loan-to-Value (LTV)
                    <QuestionMarkTooltip content="The ratio between your borrowed amount and the value of your collateral. Higher LTV increases liquidation risk." />
                  </div>
                  <div className="flex items-center gap-x-2 font-aeonikMedium text-20 text-cf-white">
                    {isLoanAccountLoading ? (
                      <SkeletonLine className="h-5 w-[100px]" />
                    ) : (
                      <>
                        {formatBps(sliderValue)}
                        <Pill color={riskColor} text={riskType} />
                      </>
                    )}
                  </div>
                </div>

                <div className="mt-10">
                  {isConfigLoading ? (
                    <SkeletonLine />
                  ) : (
                    <BorrowSlider
                      max={targetLtvThresholdBps}
                      thumbValue={sliderValue}
                      setSliderValues={handleSliderInput}
                      initialValue={loanToValueBps}
                      bpsThresholds={[targetLtvThresholdBps, softLiquidationLtvThresholdBps]}
                      labelSteps={LoanRiskTypes.length}
                      onFocus={() => setSliderFocused(true)}
                      onBlur={() => setSliderFocused(false)}
                      disabled={isPending || loanToValueBps > targetLtvThresholdBps}
                    />
                  )}
                </div>
                <Divider className="h-px pt-10" />

                <div className="grid grid-cols-2 grid-rows-2 gap-x-6 gap-y-5 pt-4">
                  <div className="flex flex-col gap-y-2">
                    <div className="flex items-center gap-x-1 font-aeonikMedium text-14 text-cf-light-2">
                      Collateral Value
                      <QuestionMarkTooltip content="The total value of assets securing your loans. Adding more increases your borrowing capacity and reduces liquidation risk." />
                    </div>
                    <div className="flex items-center gap-x-1 font-aeonikMedium text-16 text-cf-light-3">
                      {isLoanAccountLoading ? (
                        <SkeletonLine className="w-full" width={110} height={20} />
                      ) : (
                        formatUsdValue(totalCollateralBalanceUsd)
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-y-2">
                    <div className="flex items-center gap-x-1 font-aeonikMedium text-14 text-cf-light-2">
                      Borrow Capacity
                      <QuestionMarkTooltip content="The maximum amount you can borrow based on the value of your supplied assets." />
                    </div>
                    <div className="flex items-center gap-x-1 font-aeonikMedium">
                      {isLoanAccountLoading ? (
                        <SkeletonLine className="w-full" width={110} height={20} />
                      ) : (
                        <div className="flex flex-wrap items-center gap-x-1">
                          <div className="text-16 text-cf-light-3">
                            {borrowCapacity.toFormatted()} {selectedToken.symbol}
                          </div>
                          <div className="text-14 text-cf-light-2">
                            ({formatUsdValue(borrowCapacityUsd)})
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-y-2">
                    <div className="flex items-center gap-x-1 font-aeonikMedium text-14 text-cf-light-2">
                      Available liquidity
                      <QuestionMarkTooltip
                        link="https://chainflip.io/blog/lending-2.0-supply-as-collateral-yield-on-your-supplied-btc-via-boost"
                        content="Available liquidity is calculated as the pool’s liquidity multiplied by the pool’s utilization cap, minus the borrowed amount."
                      />
                    </div>
                    <div className="flex items-center gap-x-1 font-aeonikMedium">
                      {isPoolBalanceLoading ? (
                        <SkeletonLine className="w-full" width={110} height={20} />
                      ) : (
                        <div className="flex flex-wrap items-center gap-x-1">
                          <div className="text-16 text-cf-light-3">
                            {poolTotalBorrowableAmount.toFormatted()} {selectedToken.symbol}
                          </div>
                          <div className="text-14 text-cf-light-2">
                            ({formatUsdValue(poolTotalBorrowableAmountUsd)})
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-y-2">
                    <div className="flex items-center gap-x-1 font-aeonikMedium text-14 text-cf-light-2">
                      <ApyPeriodSelect
                        periods={BORROW_APY_PERIODS}
                        selected={borrowApyPeriod}
                        onChange={setBorrowApyPeriod}
                        contentClassname="!z-[100]"
                      />
                      <span>Interest Rate</span>
                      <QuestionMarkTooltip content={borrowApyTooltip} />
                    </div>
                    <div className="flex items-center gap-x-1 font-aeonikMedium text-16 text-cf-light-3">
                      {isPoolBalanceLoading || isConfigLoading ? (
                        <SkeletonLine className="w-full" width={110} height={20} />
                      ) : (
                        <InterestRateText
                          interestRateBps={totalBorrowRateBps}
                          penaltyFeePercent={penaltyFeePercent}
                          rewardsData={rewardsData}
                        />
                      )}
                    </div>
                  </div>
                </div>
                <Divider className="h-px pt-4" />
                <div className="flex flex-col gap-y-3 pt-4 font-aeonikMedium text-14 text-cf-light-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-x-1 text-14 text-cf-light-2">
                      <span className="font-aeonikMedium text-14 leading-[21px] text-cf-light-2">
                        Origination Fee
                      </span>
                      <QuestionMarkTooltip content="A one-time fee charged when opening or updating a borrow position." />
                    </div>
                    <div className="flex items-center gap-x-1">
                      <div className="flex items-center gap-x-1">
                        <span className="text-center font-aeonikMedium text-14 leading-[21px] text-cf-light-3">
                          {estimatedOriginationFee.toFormatted()}
                        </span>
                        <TokenLogo token={selectedAsset} />
                        <span className="text-center font-aeonikMedium text-14 leading-[21px] text-cf-light-3">
                          {chainflipAssetMap[selectedAsset].symbol}
                        </span>
                      </div>

                      <span className="font-aeonikMedium text-14 leading-[21px] text-cf-light-2">
                        ({formatUsdValue(estimatedOriginationFeeUsd)})
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <ReadonlyButton
                fullWidth
                onClick={ctaClickHandler}
                loading={isPending && 'iconOnly'}
                disabled={Boolean(emptyAmountError) || Boolean(error) || isPending || isLiquidating}
              >
                Confirm & Borrow
              </ReadonlyButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </DashboardModalWithCloseButton>
  );
};
